import { APP_API_V1_BASE } from "../../config/api";
// src/pages/ReportsPage.jsx
import { useState, useEffect, useCallback, useRef } from "react";
import { DateTimePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers";
import { Autocomplete, InputAdornment, Skeleton } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import {
  Box,
  Paper,
  Typography,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  TextField,
  Chip,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableContainer,
  CircularProgress,
  Snackbar,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip,
  Divider,
  LinearProgress,
  Card,
  CardContent,
} from "@mui/material";
import {
  FileDownload,
  Delete,
  Refresh,
  Add,
  Schedule,
  CheckCircle,
  ErrorOutline,
  HourglassTop,
  PlayArrow,
  Assessment,
  CheckCircleOutline,
  Insights,
  Close,
} from "@mui/icons-material";
import axios from "axios";
import { useAuth } from "../../auth/useAuth";
import ReportAgentChat from "./dashboard-agent-chat";
import dayjs from "dayjs";

const API_BASE = `${APP_API_V1_BASE}`;

// ─── Constants ────────────────────────────────────────────────
const REPORT_TYPES = [
  {
    value: "alert_summary",
    label: "Alert summary",
    hasDevice: true,
    hasSeverity: true,
  },
  {
    value: "telemetry",
    label: "Telemetry export",
    hasDevice: true,
    hasSeverity: false,
  },
  {
    value: "audit",
    label: "Audit logs",
    hasDevice: false,
    hasSeverity: false,
    hasAuditFilters: true,
    desc: "System activity, security events, and user actions log.",
  },
  {
    value: "device_health",
    label: "Device health",
    hasDevice: false,
    hasSeverity: false,
    desc: "Battery levels, connectivity, and status monitoring.",
  },
  {
    value: "firmware_compliance",
    label: "Firmware compliance",
    hasDevice: false,
    hasSeverity: false,
    desc: "Firmware version audit across all deployed devices.",
  },
  {
    value: "inventory_status",
    label: "Inventory status",
    hasDevice: false,
    hasSeverity: false,
    desc: "Deployment status and equipment inventory tracking.",
  },
  {
    value: "device_log",
    label: "Device Logs",
    hasDevice: true,
    hasSeverity: false
  }
];

const OUTPUT_FORMATS = [
  { value: "csv", label: "CSV", hint: "Raw data, best for Excel import" },
  {
    value: "excel",
    label: "Excel",
    hint: "Formatted workbook, multiple sheets",
  },
  { value: "pdf", label: "PDF", hint: "Print-ready branded layout" },
  {
    value: "json",
    label: "View in browser",
    hint: "Interactive preview, no download",
  },
];

const FREQUENCIES = ["daily", "weekly", "monthly"];

const STATUS_BADGE = {
  pending: { label: "Pending", color: "warning" },
  generating: { label: "Generating", color: "info" },
  ready: { label: "Ready", color: "success" },
  failed: { label: "Failed", color: "error" },
};

const FORMAT_LABELS = {
  csv: "CSV",
  excel: "Excel",
  pdf: "PDF",
  json: "Browser",
};

// ─── Helpers ──────────────────────────────────────────────────
const fmtDate = (iso) =>
  iso
    ? new Date(iso).toLocaleString([], {
        dateStyle: "medium",
        timeStyle: "short",
      })
    : "—";

const fmtSize = (bytes) => {
  if (!bytes) return "—";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
};

const todayStr = () => new Date().toISOString().split("T")[0];

const daysAgoStr = (n) => {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return d.toISOString().split("T")[0];
};

const VALID_SEVERITIES = ["critical", "high", "warning", "info"];
const DATE_INPUT_REGEX = /^\d{2}-\d{2}-\d{4}$/;
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const isValidIsoDate = (value) => {
  if (!value) return false;

  // If it's already a Dayjs object
  if (dayjs.isDayjs(value)) {
    return value.isValid();
  }

  // If it's a string (ISO or otherwise)
  const d = dayjs(value);
  return d.isValid();
};

// ─── Sub-components ───────────────────────────────────────────
function StatusChip({ status }) {
  const cfg = STATUS_BADGE[status] || { label: status, color: "default" };
  return <Chip label={cfg.label} color={cfg.color} size="small" />;
}

function FormatChip({ format }) {
  return (
    <Chip
      label={FORMAT_LABELS[format] || format}
      size="small"
      variant="outlined"
      sx={{ fontSize: 11 }}
    />
  );
}

// ─── Hero Banner ──────────────────────────────────────────────
function ReportsTableSkeleton({ columns, rows = 6 }) {
  return (
    <TableContainer sx={{ overflowX: "auto" }}>
      <Table>
        <TableBody>
          {Array.from({ length: rows }).map((_, rowIndex) => (
            <TableRow
              key={rowIndex}
              sx={{
                "& td": {
                  py: 1.5,
                  borderBottom: "1px solid rgba(148,163,184,0.1)",
                },
              }}
            >
              {Array.from({ length: columns }).map((__, columnIndex) => (
                <TableCell key={columnIndex}>
                  {columnIndex === 0 ? (
                    <Stack direction="row" alignItems="center" spacing={1.25}>
                      <Skeleton
                        variant="circular"
                        width={8}
                        height={8}
                        sx={{ flexShrink: 0 }}
                      />
                      <Box sx={{ width: "100%", minWidth: 120 }}>
                        <Skeleton
                          variant="text"
                          width="70%"
                          height={18}
                          sx={{ transform: "none" }}
                        />
                        <Skeleton
                          variant="text"
                          width="48%"
                          height={14}
                          sx={{ mt: 0.5, transform: "none" }}
                        />
                      </Box>
                    </Stack>
                  ) : columnIndex === columns - 1 ? (
                    <Stack
                      direction="row"
                      spacing={0.5}
                      justifyContent="flex-end"
                    >
                      <Skeleton
                        variant="rounded"
                        width={30}
                        height={30}
                        sx={{ borderRadius: 1.5 }}
                      />
                      <Skeleton
                        variant="rounded"
                        width={30}
                        height={30}
                        sx={{ borderRadius: 1.5 }}
                      />
                    </Stack>
                  ) : (
                    <Skeleton
                      variant="rounded"
                      width={columnIndex % 2 === 0 ? 92 : 118}
                      height={24}
                      sx={{ borderRadius: 999 }}
                    />
                  )}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

function ReportsHeroBanner({ tab, onTabChange, heroMetrics }) {
  return (
    <Box
      sx={{
        position: "relative",
        overflow: "hidden",
        mb: 3,
        px: { xs: 2, md: 3 },
        py: { xs: 2.5, md: 3 },
        borderRadius: 5,
        border: "1px solid rgba(148, 163, 184, 0.18)",
        background:
          "linear-gradient(135deg, rgba(15, 23, 42, 0.96), rgba(15, 118, 110, 0.88) 55%, rgba(8, 47, 73, 0.92))",
        boxShadow: "0 30px 80px rgba(15, 23, 42, 0.18)",
      }}
    >
      {/* Shimmer overlay */}
      <Box
        sx={{
          position: "absolute",
          inset: 0,
          background:
            "linear-gradient(120deg, transparent 0%, rgba(255,255,255,0.08) 38%, transparent 60%)",
          transform: "translateX(-35%)",
          pointerEvents: "none",
        }}
      />

      <Stack
        direction={{ xs: "column", lg: "row" }}
        spacing={3}
        justifyContent="space-between"
        sx={{ position: "relative", zIndex: 1 }}
      >
        {/* Left — title + tabs */}
        <Box sx={{ maxWidth: 720 }}>
          {/* <Chip
            icon={<Insights sx={{ color: "inherit !important" }} />}
            label="Operations console"
            sx={{
              mb: 2,
              color: "#DDF4F1",
              backgroundColor: "rgba(255,255,255,0.12)",
              border: "1px solid rgba(255,255,255,0.16)",
            }}
          /> */}
          <Typography
            sx={{
              color: "#F8FAFC",
              fontSize: { xs: 28, md: 40 },
              fontWeight: 700,
              lineHeight: 1.05,
              letterSpacing: "-0.03em",
            }}
          >
            Reports
          </Typography>
          <Typography
            sx={{
              mt: 1.5,
              maxWidth: 580,
              color: "rgba(226, 232, 240, 0.86)",
              fontSize: { xs: 14, md: 16 },
              lineHeight: 1.7,
            }}
          >
            Generate, download, and schedule reports for your organisation —
            delivered on demand or automatically by email.
          </Typography>

          {/* Tab pills */}
          <Stack
            direction="row"
            spacing={1}
            useFlexGap
            flexWrap="wrap"
            sx={{ mt: 3 }}
          >
            {["Generate", "History", "Schedules"].map((label, idx) => (
              <Button
                key={label}
                variant={tab === idx ? "contained" : "outlined"}
                onClick={() => onTabChange(idx)}
                sx={{
                  px: 2.2,
                  py: 1,
                  borderRadius: 999,
                  textTransform: "none",
                  fontWeight: 700,
                  ...(tab === idx
                    ? {
                        color: "#082F49",
                        background: "linear-gradient(135deg, #FDE68A, #F59E0B)",
                        boxShadow: "0 16px 30px rgba(245, 158, 11, 0.28)",
                        border: "none",
                      }
                    : {
                        color: "#E2E8F0",
                        borderColor: "rgba(226, 232, 240, 0.2)",
                        backgroundColor: "rgba(255,255,255,0.04)",
                      }),
                }}
              >
                {label}
              </Button>
            ))}
          </Stack>
        </Box>

        {/* Right — metric cards */}
        <Stack
          spacing={1.5}
          sx={{ minWidth: { lg: 280 }, width: { xs: "100%", lg: "auto" } }}
        >
          {heroMetrics.map((metric) => (
            <Card
              key={metric.label}
              sx={{
                borderRadius: 4,
                color: "#F8FAFC",
                background: metric.bg,
                border: "1px solid rgba(255,255,255,0.12)",
                backdropFilter: "blur(10px)",
                boxShadow: "none",
              }}
            >
              <CardContent sx={{ px: 2.25, py: 2, "&:last-child": { pb: 2 } }}>
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                >
                  <Box>
                    <Typography
                      sx={{ color: "rgba(226,232,240,0.72)", fontSize: 13 }}
                    >
                      {metric.label}
                    </Typography>
                    <Typography sx={{ mt: 0.5, fontSize: 28, fontWeight: 700 }}>
                      {metric.value}
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      width: 44,
                      height: 44,
                      display: "grid",
                      placeItems: "center",
                      borderRadius: 3,
                      color: metric.tone,
                      backgroundColor: "rgba(255,255,255,0.78)",
                    }}
                  >
                    {metric.icon}
                  </Box>
                </Stack>
              </CardContent>
            </Card>
          ))}
        </Stack>
      </Stack>
    </Box>
  );
}

// ─── Generate Tab ─────────────────────────────────────────────
function GenerateTab({ headers, onGenerated, activeOrg, onToast }) {
  const [reportType, setReportType] = useState("alert_summary");
  // single selected format (radio behaviour)
  const [formats, setFormats] = useState("csv");
  // const [dateFrom, setDateFrom] = useState(daysAgoStr(30));
  // const [dateTo, setDateTo] = useState(todayStr());
  const [deviceOptions, setDeviceOptions] = useState([]);
  const [deviceLoading, setDeviceLoading] = useState(false);
  const [deviceLoadError, setDeviceLoadError] = useState("");
  const [severity, setSeverity] = useState("");
  const [loading, setLoading] = useState(false);
  const [activeReport, setActiveReport] = useState(null);
  const [inputValue, setInputValue] = useState("");
  const [open, setOpen] = useState(false);
  const [selectedDevices, setSelectedDevices] = useState([]);
  const [dateFrom, setDateFrom] = useState(dayjs());
  const [dateTo, setDateTo] = useState(dayjs());
  const [entityLoading, setEntityLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);

  const pollRef = useRef(null);
  const [entityTypes, setEntityTypes] = useState([]);
  const [actions, setActions] = useState([]);
  const [entityType, setEntityType] = useState("");
  const [action, setAction] = useState("");
  useEffect(() => {
    if (reportType === "audit") {
      setEntityLoading(true);
      axios
        .get(`${API_BASE}/reports/audit/filters`, { headers })
        .then((res) => {
          setEntityTypes(res.data.entity_types || []);
          setActions(res.data.actions || []);
        })
        .catch((err) => console.error(err))
        .finally(() => setEntityLoading(false));
      return;
    }
    setEntityTypes([]);
    setActions([]);
    setEntityType("");
    setAction("");
  }, [reportType, headers]);
  useEffect(() => {
    if (reportType === "audit" && entityType) {
      setActionLoading(true);
      axios
        .get(`${API_BASE}/reports/audit/filters`, {
          headers,
          params: { entity_type: entityType },
        })
        .then((res) => {
          setActions(res.data.actions || []);
        })
        .catch((err) => console.error(err))
        .finally(() => setActionLoading(false));
      return;
    }
    setAction("");
  }, [entityType, reportType, headers]);

  const typeMeta =
    REPORT_TYPES.find((t) => t.value === reportType) || REPORT_TYPES[0];
  const shouldHideFilters =
    reportType === "audit" &&
    (typeMeta.allowedEntities || []).includes(entityType);

  useEffect(() => {
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, []);

  useEffect(() => {
    if (!typeMeta.hasSeverity && severity) {
      setSeverity("");
    }
  }, [typeMeta.hasSeverity, severity]);

  useEffect(() => {
    if (!activeOrg?.id) return;

    const loadDevices = async () => {
      try {
        setDeviceLoading(true);
        setDeviceLoadError("");

        const res = await axios.get(`${API_BASE}/devices`, { headers });
        const rows = Array.isArray(res?.data) ? res.data : [];
        const normalized = rows
          .map((device) => ({
            id: String(device?.id || "").trim(),
            name: String(device?.name || "").trim(),
          }))
          .filter((device) => device.id);

        setDeviceOptions(normalized);
      } catch (err) {
        setDeviceLoadError(
          err?.response?.data?.error || "Unable to load devices",
        );
        onToast?.(
          err?.response?.data?.error ||
            "Unable to load devices for report filters.",
          "error",
        );
        setDeviceOptions([]);
      } finally {
        setDeviceLoading(false);
      }
    };

    loadDevices();
  }, [activeOrg?.id, headers, onToast]);

  const startPolling = useCallback(
    (reportId) => {
      if (pollRef.current) clearInterval(pollRef.current);
      pollRef.current = setInterval(async () => {
        try {
          const res = await axios.get(`${API_BASE}/reports/${reportId}`, {
            headers,
          });
          const report = res.data;
          setActiveReport(report);

          if (report.status === "ready" || report.status === "failed") {
            clearInterval(pollRef.current);
            pollRef.current = null;
            if (report.status === "ready") {
              onToast?.("Report is ready for download.", "success");
            } else {
              onToast?.(
                report?.error_message || "Report generation failed.",
                "error",
              );
            }
            onGenerated();
          }
        } catch (err) {
          console.error("Polling failed", err);
          onToast?.(
            "Stopped polling report status due to a network issue.",
            "error",
          );
          clearInterval(pollRef.current);
        }
      }, 3000);
    },
    [headers, onGenerated, onToast],
  );

  // single-select: clicking a format simply selects it
  const toggleFormat = (val) => setFormats(val);

  const handleGenerate = async () => {
    if (!activeOrg?.id) {
      onToast?.("No active organization selected.", "error");
      return;
    }

    if (!REPORT_TYPES.some((t) => t.value === reportType)) {
      onToast?.("Please select a valid report type.", "error");
      return;
    }

    if (!OUTPUT_FORMATS.some((f) => f.value === formats)) {
      onToast?.("Please select a valid output format.", "error");
      return;
    }

    if (!isValidIsoDate(dateFrom) || !isValidIsoDate(dateTo)) {
      onToast?.("Please provide valid dates in DD-MM-YYYY format.", "error");
      return;
    }

    if (dayjs(dateFrom).isAfter(dayjs(dateTo))) {
      onToast?.(
        "Date range is invalid: 'From' date cannot be after 'To' date.",
        "error",
      );
      return;
    }

    if (
      selectedDevices.length > 0 &&
      !selectedDevices.every((dev) =>
        deviceOptions.some((d) => d.id === dev.id),
      )
    ) {
      onToast?.(
        "Selected device is invalid for the current organization.",
        "error",
      );
      return;
    }

    if (severity && reportType !== "alert_summary") {
      onToast?.(
        "Severity filter is only supported for Alert summary reports.",
        "error",
      );
      return;
    }

    if (severity && !VALID_SEVERITIES.includes(severity)) {
      onToast?.("Please select a valid severity value.", "error");
      return;
    }

    setLoading(true);
    setActiveReport(null);

    try {
      const deviceIds = [
        ...new Set(
          selectedDevices
            .map((device) => String(device?.id || "").trim())
            .filter(Boolean),
        ),
      ];

      // ✅ COMMON PARAMS
      const baseParams = {
        date_from: dayjs(dateFrom).format("YYYY-MM-DDTHH:mm:ss.SSSZ"),
        date_to: dayjs(dateTo).format("YYYY-MM-DDTHH:mm:ss.SSSZ"),
      };

      if (severity) baseParams.severity = severity;

      // ✅ AUDIT FILTERS (FIXED)
      if (reportType === "audit") {
        if (entityType) baseParams.entity_type = entityType;
        if (action) baseParams.action = action;
      }

      console.log("BASE PARAMS:", baseParams);

      // 👉 SINGLE REQUEST (no device OR single device)
      if (deviceIds.length <= 1) {
        if (deviceIds.length === 1) {
          baseParams.device_id = deviceIds[0];
        }

        console.log("FINAL PARAMS:", baseParams);

        const res = await axios.post(
          `${API_BASE}/reports/generate/${activeOrg.id}`,
          {
            report_type: reportType,
            output_format: formats,
            parameters: baseParams,
          },
          { headers },
        );

        const report = res.data.report;
        setActiveReport(report);
        startPolling(report.id);
        return;
      }

      // 👉 MULTI DEVICE (FIXED)
      let lastReport = null;

      for (const id of deviceIds) {
        const params = {
          ...baseParams,
          device_id: id,
        };

        console.log("MULTI PARAMS:", params);

        const res = await axios.post(
          `${API_BASE}/reports/generate/${activeOrg.id}`,
          {
            report_type: reportType,
            output_format: formats,
            parameters: params,
          },
          { headers },
        );

        lastReport = res.data.report;
      }

      if (lastReport) {
        setActiveReport(lastReport);
        startPolling(lastReport.id);
      }
    } catch (err) {
      console.error("Generate failed", err);
      onToast?.(
        err?.response?.data?.error || "Failed to generate report.",
        "error",
      );
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async () => {
    if (!activeReport?.download_url) {
      onToast?.("Download link is not available yet.", "warning");
      return;
    }

    try {
      window.open(activeReport.download_url, "_blank");
    } catch (err) {
      onToast?.("Failed to open download link.", "error");
    }
  };

  // format-specific accent colours
  const FORMAT_META = {
    csv: {
      color: "#10B981",
      bg: "rgba(16,185,129,0.10)",
      border: "rgba(16,185,129,0.30)",
    },
    excel: {
      color: "#0EA5E9",
      bg: "rgba(14,165,233,0.10)",
      border: "rgba(14,165,233,0.30)",
    },
    pdf: {
      color: "#F59E0B",
      bg: "rgba(245,158,11,0.10)",
      border: "rgba(245,158,11,0.30)",
    },
    json: {
      color: "#A78BFA",
      bg: "rgba(167,139,250,0.10)",
      border: "rgba(167,139,250,0.30)",
    },
  };

  const SectionLabel = ({ children }) => (
    <Typography
      sx={{
        fontSize: 10,
        fontWeight: 700,
        letterSpacing: "0.1em",
        textTransform: "uppercase",
        color: "rgba(100,116,139,0.9)",
        mb: 1.5,
      }}
    >
      {children}
    </Typography>
  );

  return (
    <Box
      display="grid"
      gridTemplateColumns={{ xs: "1fr", md: "1fr 300px" }}
      gap={2.5}
      sx={{ alignItems: "start" }}
    >
      {/* ── LEFT: Report configuration ── */}
      <Paper
        elevation={0}
        sx={{
          borderRadius: 3,
          overflow: "hidden",
          border: "1px solid rgba(148,163,184,0.18)",
          boxShadow: "0 4px 24px rgba(15,23,42,0.07)",
        }}
      >
        {/* Card header stripe */}
        <Box
          sx={{
            px: 3,
            py: 2.5,
            background:
              "linear-gradient(135deg, rgba(15,23,42,0.96), rgba(15,118,110,0.88) 60%, rgba(8,47,73,0.92))",
            position: "relative",
            overflow: "hidden",
          }}
        >
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              background:
                "linear-gradient(120deg, transparent 0%, rgba(255,255,255,0.06) 40%, transparent 65%)",
              pointerEvents: "none",
            }}
          />
          <Stack
            direction="row"
            alignItems="center"
            spacing={1.5}
            sx={{ position: "relative", zIndex: 1, minWidth: 0 }}
          >
            <Box
              sx={{
                width: 36,
                height: 36,
                borderRadius: 2,
                display: "grid",
                placeItems: "center",
                background: "rgba(255,255,255,0.12)",
                border: "1px solid rgba(255,255,255,0.18)",
              }}
            >
              <Assessment sx={{ fontSize: 18, color: "#7DD3FC" }} />
            </Box>
            <Box>
              <Typography
                sx={{
                  color: "#F8FAFC",
                  fontWeight: 700,
                  fontSize: 15,
                  lineHeight: 1.2,
                  fontSize: { xs: 15, md: 20 }
                }}
              >
                Report configuration
              </Typography>
              <Typography
                sx={{ color: "rgba(226,232,240,0.65)", fontSize: 12, mt: 0.2 }}
              >
                Choose type, filters, and output format
              </Typography>
            </Box>
          </Stack>
        </Box>

        {/* Form body */}
        <Box sx={{ p: { xs: 2, sm: 3 } }}>
          <TextField
            select
            label="Report Type"
            value={reportType}
            onChange={(e) => setReportType(e.target.value)}
            fullWidth
            size="small"
            sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 }, mb: 3 }}
          >
            {REPORT_TYPES.map((t) => (
              <MenuItem
                key={t.value}
                value={t.value}
                sx={{
                  fontSize: 13,
                  py: 1.2,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "flex-start",
                  gap: 0.3,
                }}
              >
                <Typography fontSize={14} fontWeight={500}>
                  {t.label}
                </Typography>
                <Typography fontSize={11} color="#64748B">
                  {t.desc}
                </Typography>
              </MenuItem>
            ))}
          </TextField>
          {reportType === "audit" && (
            <Box
              sx={{ "& .MuiOutlinedInput-root": { borderRadius: 3 }, mb: 2 }}
            >
              {/* Entity Type */}
              <Select
                fullWidth
                disabled={entityLoading}
                value={entityType}
                onChange={(e) => setEntityType(e.target.value)}
                displayEmpty
              >
                <MenuItem value="">
                  {entityLoading ? "Loading..." : "All Entity Types"}
                </MenuItem>
                {entityTypes.map((et) => (
                  <MenuItem key={et} value={et}>
                    {et}
                  </MenuItem>
                ))}
              </Select>

              {/* Action */}
              <Select
                fullWidth
                disabled={!entityType || actionLoading}
                value={action}
                onChange={(e) => setAction(e.target.value)}
                displayEmpty
                sx={{ mt: 2 }}
              >
                <MenuItem value="">
                  {actionLoading ? "Loading..." : "All Actions"}
                </MenuItem>
                {actions.map((ac) => (
                  <MenuItem key={ac} value={ac}>
                    {ac}
                  </MenuItem>
                ))}
              </Select>
            </Box>
          )}
          {/* ── Date range ── */}
          <SectionLabel>Date range</SectionLabel>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <Box
              display="grid"
              gridTemplateColumns={{ xs: "1fr", sm: "1fr 1fr" }}
              gap={2}
              mb={3}
            >
              {/* FROM */}
              <DateTimePicker
                label="From"
                value={dateFrom}
                onChange={(newValue) => setDateFrom(newValue)}
                ampm={true} 
                slotProps={{
                  textField: {
                    size: "small",
                    fullWidth: true,
                    sx: {
                      "& .MuiOutlinedInput-root": { borderRadius: 2 },
                    },
                  },
                }}
              />

              {/* TO */}
              <DateTimePicker
                label="To"
                value={dateTo}
                onChange={(newValue) => setDateTo(newValue)}
                ampm={true}
                minDateTime={dateFrom} 
                slotProps={{
                  textField: {
                    size: "small",
                    fullWidth: true,
                    sx: {
                      "& .MuiOutlinedInput-root": { borderRadius: 2 },
                    },
                  },
                }}
              />
            </Box>
          </LocalizationProvider>

          {/* ── Optional filters ── */}
          {!shouldHideFilters &&
            (typeMeta.hasDevice || typeMeta.hasSeverity) && (
              <>
                <SectionLabel>Filters</SectionLabel>

                {typeMeta.hasDevice && (
                  <>
                    <Autocomplete
                      multiple
                      size="small"
                      fullWidth
                      options={deviceOptions}
                      loading={deviceLoading}
                      popupIcon={null}
                      value={selectedDevices}
                      onChange={(e, newValue) => setSelectedDevices(newValue)}
                      inputValue={inputValue}
                      onInputChange={(e, value) => {
                        setInputValue(value);
                        setOpen(true);
                      }}
                      open={open}
                      onClose={() => setOpen(false)}
                      filterOptions={(options, { inputValue }) =>
                        options.filter((option) =>
                          (option.name + " " + option.id)
                            .toLowerCase()
                            .includes(inputValue.toLowerCase()),
                        )
                      }
                      getOptionLabel={(option) =>
                        option.name
                          ? `${option.name} (${option.id})`
                          : option.id
                      }
                      isOptionEqualToValue={(option, value) =>
                        option.id === value.id
                      }
                      renderTags={(value, getTagProps) =>
                        value.map((option, index) => (
                          <Chip
                            {...getTagProps({ index })}
                            label={option.name || option.id}
                            size="small"
                          />
                        ))
                      }
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          placeholder="Search devices..."
                        />
                      )}
                    />

                    <Typography sx={{ mb: 2, fontSize: 12 }}>
                      {deviceLoading
                        ? "Loading devices..."
                        : deviceLoadError
                          ? deviceLoadError
                          : `${deviceOptions.length} device${
                              deviceOptions.length === 1 ? "" : "s"
                            } available`}
                    </Typography>
                  </>
                )}

                {typeMeta.hasSeverity && (
                  <FormControl fullWidth size="small" sx={{ mb: 3 }}>
                    <InputLabel>Severity filter</InputLabel>
                    <Select
                      value={severity}
                      label="Severity filter"
                      onChange={(e) => setSeverity(e.target.value)}
                    >
                      <MenuItem value="">All severities</MenuItem>
                      {["critical", "high", "warning", "info"].map((s) => (
                        <MenuItem key={s} value={s}>
                          {s.charAt(0).toUpperCase() + s.slice(1)}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}
              </>
            )}

          {/* ── Output formats (SINGLE-SELECT) ── */}
          <SectionLabel>Output formats</SectionLabel>
          <Box
            display="grid"
            gridTemplateColumns={{ xs: "1fr", sm: "1fr 1fr" }}
            gap={1}
            mb={3}
          >
            {OUTPUT_FORMATS.map((f) => {
              // single-select: active when this format is the selected one
              const active = formats === f.value;
              const meta = FORMAT_META[f.value];
              return (
                <Box
                  key={f.value}
                  onClick={() => toggleFormat(f.value)}
                  sx={{
                    px: 2,
                    py: 1.25,
                    borderRadius: 2,
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    border: active
                      ? `1.5px solid ${meta.border}`
                      : "1.5px solid rgba(148,163,184,0.2)",
                    background: active ? meta.bg : "rgba(248,250,252,0.6)",
                    transition: "all 0.15s ease",
                    "&:hover": {
                      borderColor: meta.border,
                      background: meta.bg,
                    },
                  }}
                >
                  {/* radio indicator */}
                  <Box
                    sx={{
                      width: 15,
                      height: 15,
                      borderRadius: "50%",
                      flexShrink: 0,
                      display: "grid",
                      placeItems: "center",
                      border: active
                        ? `2px solid ${meta.color}`
                        : "2px solid #CBD5E1",
                      background: "transparent",
                      transition: "all 0.15s ease",
                    }}
                  >
                    {active && (
                      <Box
                        sx={{
                          width: 7,
                          height: 7,
                          borderRadius: "50%",
                          background: meta.color,
                        }}
                      />
                    )}
                  </Box>
                  <Box>
                    <Typography
                      sx={{
                        fontSize: 12,
                        fontWeight: active ? 700 : 500,
                        color: active ? meta.color : "#334155",
                      }}
                    >
                      {f.label}
                    </Typography>
                  </Box>
                </Box>
              );
            })}
          </Box>

          {/* ── CTA ── */}
          <Button
            variant="contained"
            fullWidth
            onClick={handleGenerate}
            disabled={
              loading || activeReport?.status === "generating" || !formats
            }
            startIcon={
              loading ? (
                <CircularProgress size={15} color="inherit" />
              ) : (
                <PlayArrow />
              )
            }
            sx={{
              py: 1.3,
              borderRadius: 999,
              textTransform: "none",
              fontWeight: 700,
              fontSize: 14,
              color: "#fff",
              background: "linear-gradient(135deg, #0F766E, #0EA5E9)",
              boxShadow: "0 8px 24px rgba(15,118,110,0.28)",
              "&:hover": {
                background: "linear-gradient(135deg, #0D6660, #0C96D6)",
              },
              "&.Mui-disabled": {
                color: "#fff !important",
                WebkitTextFillColor: "#fff", // 👈 fixes Chrome override
                opacity: 0.7,
              },
            }}
          >
            {loading ? "Submitting…" : "Generate report"}
          </Button>
        </Box>
      </Paper>

      {/* ── RIGHT: status panel + format guide ── */}
      <Box display="flex" flexDirection="column" gap={2}>
        {/* Active report status */}
        {activeReport &&
          (() => {
            const isReady = activeReport.status === "ready";
            const isFailed = activeReport.status === "failed";
            const isProcessing = !isReady && !isFailed;

            // ── Resolve format-specific colours (used by both the card and the download button) ──
            const dlMeta = FORMAT_META[activeReport.output_format] ?? {
              color: "#10B981",
              bg: "rgba(16,185,129,0.08)",
              border: "rgba(16,185,129,0.5)",
            };

            const statusCfg = {
              // ── CHANGED: ready card now inherits the format colour ──
              ready: {
                border: dlMeta.border,
                bg: dlMeta.bg,
                icon: (
                  <CheckCircle sx={{ fontSize: 18, color: dlMeta.color }} />
                ),
                label: "Report ready",
                labelColor: dlMeta.color,
              },
              failed: {
                border: "rgba(239,68,68,0.5)",
                bg: "rgba(239,68,68,0.06)",
                icon: <ErrorOutline sx={{ fontSize: 18, color: "#EF4444" }} />,
                label: "Generation failed",
                labelColor: "#EF4444",
              },
              generating: {
                border: "rgba(14,165,233,0.35)",
                bg: "rgba(14,165,233,0.04)",
                icon: <HourglassTop sx={{ fontSize: 18, color: "#0EA5E9" }} />,
                label: "Generating…",
                labelColor: "#0EA5E9",
              },
              pending: {
                border: "rgba(14,165,233,0.35)",
                bg: "rgba(14,165,233,0.04)",
                icon: <HourglassTop sx={{ fontSize: 18, color: "#0EA5E9" }} />,
                label: "Queued…",
                labelColor: "#0EA5E9",
              },
            }[activeReport.status] || {
              border: "rgba(148,163,184,0.3)",
              bg: "transparent",
              icon: null,
              label: activeReport.status,
              labelColor: "#64748B",
            };

            return (
              <Paper
                elevation={0}
                sx={{
                  borderRadius: 3,
                  border: `1.5px solid ${statusCfg.border}`,
                  background: statusCfg.bg,
                  overflow: "hidden",
                  boxShadow: "0 4px 16px rgba(15,23,42,0.06)",
                }}
              >
                <Box sx={{ px: 2.5, pt: 2.5, pb: isProcessing ? 1 : 2.5 }}>
                  <Stack direction="row" alignItems="center" spacing={1} mb={1}>
                    {statusCfg.icon}
                    <Typography
                      sx={{
                        fontSize: 13,
                        fontWeight: 700,
                        color: statusCfg.labelColor,
                      }}
                    >
                      {statusCfg.label}
                    </Typography>
                  </Stack>

                  <Typography
                    sx={{ fontSize: 11.5, color: "#64748B", lineHeight: 1.5 }}
                  >
                    {activeReport.file_name ||
                      `${activeReport.report_type} · ${activeReport.output_format}`}
                    {activeReport.file_size
                      ? ` · ${fmtSize(activeReport.file_size)}`
                      : ""}
                  </Typography>

                  {isReady && (
                    <Button
                      size="small"
                      fullWidth
                      variant="outlined"
                      startIcon={<FileDownload sx={{ fontSize: 15 }} />}
                      onClick={handleDownload}
                      sx={{
                        mt: 1.5,
                        borderRadius: 999,
                        textTransform: "none",
                        fontWeight: 600,
                        fontSize: 12,
                        // ── CHANGED: border, text colour and hover tint now
                        //    derive from the active report's output_format ──
                        borderColor: dlMeta.border,
                        color: dlMeta.color,
                        "&:hover": {
                          background: dlMeta.bg,
                          borderColor: dlMeta.color,
                        },
                      }}
                    >
                      Download {FORMAT_LABELS[activeReport.output_format]}
                    </Button>
                  )}

                  {isFailed && (
                    <Typography
                      sx={{ mt: 1, fontSize: 11.5, color: "#EF4444" }}
                    >
                      {activeReport.error_message ||
                        "An unexpected error occurred."}
                    </Typography>
                  )}
                </Box>

                {isProcessing && (
                  <LinearProgress
                    sx={{
                      height: 3,
                      "& .MuiLinearProgress-bar": {
                        background: "linear-gradient(90deg, #0F766E, #0EA5E9)",
                      },
                      background: "rgba(14,165,233,0.1)",
                    }}
                  />
                )}
              </Paper>
            );
          })()}

        {/* Format guide — dark glass card */}
        <Paper
          elevation={0}
          sx={{
            borderRadius: 3,
            overflow: "hidden",
            border: "1px solid rgba(148,163,184,0.16)",
            boxShadow: "0 4px 24px rgba(15,23,42,0.07)",
          }}
        >
          {/* Dark header */}
          <Box
            sx={{
              px: 2.5,
              py: 2,
              background:
                "linear-gradient(135deg, rgba(15,23,42,0.96), rgba(15,118,110,0.88) 60%, rgba(8,47,73,0.92))",
              position: "relative",
              overflow: "hidden",
            }}
          >
            <Box
              sx={{
                position: "absolute",
                inset: 0,
                background:
                  "linear-gradient(120deg, transparent 0%, rgba(255,255,255,0.06) 40%, transparent 65%)",
                pointerEvents: "none",
              }}
            />
            <Typography
              sx={{
                color: "#F8FAFC",
                fontWeight: 600,
                fontSize: 13,
                position: "relative",
                zIndex: 1,
                fontSize: { xs: 15, md: 20 }
              }}
            >
              Format guide
            </Typography>
            <Typography
              sx={{
                color: "rgba(226,232,240,0.6)",
                fontSize: 11,
                mt: 0.25,
                position: "relative",
                zIndex: 1,
              }}
            >
              Pick the right format for your workflow
            </Typography>
          </Box>

          {/* Format rows */}
          <Box sx={{ p: 2 }}>
            <Stack spacing={0.75}>
              {OUTPUT_FORMATS.map((f) => {
                const meta = FORMAT_META[f.value];
                // single-select: active when this format is the selected one
                const active = formats === f.value;
                return (
                  <Box
                    key={f.value}
                    onClick={() => toggleFormat(f.value)}
                    sx={{
                      px: 1.75,
                      py: 1.25,
                      borderRadius: 3,
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      gap: 1.5,
                      border: active
                        ? `1px solid ${meta.border}`
                        : "1px solid transparent",
                      background: active ? meta.bg : "transparent",
                      transition: "all 0.15s ease",
                      "&:hover": {
                        background: meta.bg,
                        border: `1px solid ${meta.border}`,
                      },
                    }}
                  >
                    <Box
                      sx={{
                        width: 35,
                        height: 28,
                        borderRadius: 1.5,
                        flexShrink: 0,
                        display: "grid",
                        placeItems: "center",
                        background: active ? meta.bg : "rgba(241,245,249,0.8)",
                        border: `1px solid ${active ? meta.border : "rgba(226,232,240,0.6)"}`,
                      }}
                    >
                      <Typography
                        sx={{
                          fontSize: 9,
                          fontWeight: 800,
                          color: meta.color,
                          letterSpacing: "0.02em",
                        }}
                      >
                        {f.value === "json" ? "{ }" : f.value.toUpperCase()}
                      </Typography>
                    </Box>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography
                        sx={{
                          fontSize: 12,
                          fontWeight: active ? 700 : 600,
                          color: active ? meta.color : "#334155",
                        }}
                      >
                        {f.label}
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: 10.5,
                          color: "#94A3B8",
                          lineHeight: 1.35,
                        }}
                      >
                        {f.hint}
                      </Typography>
                    </Box>
                    {/* radio indicator */}
                    <Box
                      sx={{
                        width: 16,
                        height: 16,
                        borderRadius: "50%",
                        flexShrink: 0,
                        display: "grid",
                        placeItems: "center",
                        border: active
                          ? `2px solid ${meta.color}`
                          : "2px solid #CBD5E1",
                        background: "transparent",
                        transition: "all 0.15s ease",
                      }}
                    >
                      {active && (
                        <Box
                          sx={{
                            width: 8,
                            height: 8,
                            borderRadius: "50%",
                            background: meta.color,
                          }}
                        />
                      )}
                    </Box>
                  </Box>
                );
              })}
            </Stack>
          </Box>
        </Paper>
      </Box>
    </Box>
  );
}

// ─── History Tab ──────────────────────────────────────────────
function HistoryTab({ headers, refresh, onRefresh, onToast, activeOrg }) {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [typeFilter, setTypeFilter] = useState("");
  const [deleteId, setDeleteId] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const PAGE_SIZE = 20;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page, page_size: PAGE_SIZE });
      if (typeFilter) params.set("report_type", typeFilter);
      const res = await axios.get(`${API_BASE}/reports?${params}`, { headers });
      setReports(res.data.data);
      setTotal(res.data.pagination.total);
    } catch (err) {
      console.error("Load reports failed", err);
      onToast?.(
        err?.response?.data?.error || "Failed to load report history.",
        "error",
      );
    } finally {
      setLoading(false);
    }
  }, [page, typeFilter, headers, onToast]);

  useEffect(() => {
    load();
  }, [load, refresh]);

  const handleDownload = async (report) => {
    try {
      const res = await axios.get(`${API_BASE}/reports/${report.id}`, {
        headers,
      });
      const url = res.data.download_url || res.data.file_url;
      if (url) {
        window.open(url, "_blank");
        onToast?.("Download started in a new tab.", "success");
        return;
      }
      onToast?.("No download URL is available for this report.", "warning");
    } catch (err) {
      console.error("Download failed", err);
      onToast?.(
        err?.response?.data?.error || "Failed to download report.",
        "error",
      );
    }
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    setDeleteLoading(true);
    try {
      await axios.delete(`${API_BASE}/reports/${deleteId}`, { headers });
      setDeleteId(null);
      onRefresh();
      load();
      onToast?.("Report deleted successfully.", "success");
    } catch (err) {
      console.error("Delete failed", err);
      onToast?.(
        err?.response?.data?.error || "Failed to delete report.",
        "error",
      );
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleRetry = async (report) => {
    if (!activeOrg?.id) {
      onToast?.("No active organization selected.", "error");
      return;
    }

    try {
      await axios.post(
        `${API_BASE}/reports/generate/${activeOrg.id}`,
        {
          report_type: report.report_type,
          output_format: report.output_format,
          parameters: report.parameters,
        },
        { headers },
      );
      load();
      onToast?.("Report retry started successfully.", "success");
    } catch (err) {
      console.error("Retry failed", err);
      onToast?.(
        err?.response?.data?.error || "Failed to retry report generation.",
        "error",
      );
    }
  };

  // per-status row accent
  const STATUS_ROW = {
    ready: {
      dot: "#10B981",
      bg: "rgba(16,185,129,0.06)",
      border: "rgba(16,185,129,0.18)",
    },
    failed: {
      dot: "#EF4444",
      bg: "rgba(239,68,68,0.06)",
      border: "rgba(239,68,68,0.18)",
    },
    generating: {
      dot: "#0EA5E9",
      bg: "rgba(14,165,233,0.06)",
      border: "rgba(14,165,233,0.18)",
    },
    pending: {
      dot: "#F59E0B",
      bg: "rgba(245,158,11,0.06)",
      border: "rgba(245,158,11,0.18)",
    },
  };

  const FORMAT_COLOR = {
    csv: "#10B981",
    excel: "#0EA5E9",
    pdf: "#F59E0B",
    json: "#A78BFA",
  };

  return (
    <Paper
      elevation={0}
      sx={{
        borderRadius: 3,
        overflow: "hidden",
        border: "1px solid rgba(148,163,184,0.18)",
        boxShadow: "0 4px 24px rgba(15,23,42,0.07)",
      }}
    >
      {/* ── Dark header stripe ── */}
      <Box
        sx={{
          px: 3,
          py: 2.5,
          background:
            "linear-gradient(135deg, rgba(15,23,42,0.96), rgba(15,118,110,0.88) 60%, rgba(8,47,73,0.92))",
          position: "relative",
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            background:
              "linear-gradient(120deg, transparent 0%, rgba(255,255,255,0.06) 40%, transparent 65%)",
            pointerEvents: "none",
          }}
        />
        <Stack
          direction={{ xs: "column", sm: "row" }}
          justifyContent="space-between"
          alignItems={{ sm: "center" }}
          spacing={2}
          sx={{ position: "relative", zIndex: 1 }}
        >
          <Stack direction="row" alignItems="center" spacing={1.5}>
            <Box
              sx={{
                width: 36,
                height: 36,
                borderRadius: 2,
                display: "grid",
                placeItems: "center",
                background: "rgba(255,255,255,0.12)",
                border: "1px solid rgba(255,255,255,0.18)",
              }}
            >
              <Schedule sx={{ fontSize: 18, color: "#7DD3FC" }} />
            </Box>
            <Box>
              <Typography
                sx={{
                  color: "#F8FAFC",
                  fontWeight: 700,
                  fontSize: 15,
                  lineHeight: 1.2,
                }}
              >
                Report history
              </Typography>
              <Typography
                sx={{ color: "rgba(226,232,240,0.65)", fontSize: 12, mt: 0.2 }}
              >
                {total > 0
                  ? `${total} report${total !== 1 ? "s" : ""} · files expire after 30 days`
                  : "Files expire after 30 days"}
              </Typography>
            </Box>
          </Stack>

          {/* Filter + refresh */}
          <Stack direction="row" spacing={1} alignItems="center">
            <Select
              value={typeFilter}
              size="small"
              onChange={(e) => {
                setTypeFilter(e.target.value);
                setPage(1);
              }}
              displayEmpty
              sx={{
                fontSize: 12,
                minWidth: 148,
                borderRadius: 999,
                color: "#E2E8F0",
                "& .MuiOutlinedInput-notchedOutline": {
                  borderColor: "rgba(226,232,240,0.22)",
                },
                "& .MuiSelect-icon": { color: "#94A3B8" },
                background: "rgba(255,255,255,0.07)",
                "&:hover .MuiOutlinedInput-notchedOutline": {
                  borderColor: "rgba(226,232,240,0.4)",
                },
              }}
            >
              <MenuItem value="">All types</MenuItem>
              {REPORT_TYPES.map((t) => (
                <MenuItem key={t.value} value={t.value}>
                  {t.label}
                </MenuItem>
              ))}
            </Select>
            <IconButton
              size="small"
              onClick={load}
              sx={{
                color: "rgba(226,232,240,0.7)",
                border: "1px solid rgba(226,232,240,0.2)",
                borderRadius: 2,
                p: 0.75,
                "&:hover": { background: "rgba(255,255,255,0.1)" },
              }}
            >
              <Refresh sx={{ fontSize: 17 }} />
            </IconButton>
          </Stack>
        </Stack>
      </Box>

      {/* ── Body ── */}
      <Box>
        {loading ? (
          <Box sx={{ background: "#fff" }}>
            <ReportsTableSkeleton columns={7} />
          </Box>
        ) : reports.length === 0 ? (
          <Box
            sx={{
              py: 7,
              textAlign: "center",
              background:
                "radial-gradient(ellipse at center, rgba(15,118,110,0.04) 0%, transparent 70%)",
            }}
          >
            <Box
              sx={{
                width: 48,
                height: 48,
                borderRadius: 3,
                background: "rgba(15,118,110,0.08)",
                border: "1px solid rgba(15,118,110,0.15)",
                display: "grid",
                placeItems: "center",
                mx: "auto",
                mb: 2,
              }}
            >
              <Assessment sx={{ fontSize: 22, color: "#0F766E" }} />
            </Box>
            <Typography
              sx={{ fontWeight: 600, fontSize: 14, color: "#334155", mb: 0.5 }}
            >
              No reports yet
            </Typography>
            <Typography sx={{ fontSize: 13, color: "#94A3B8" }}>
              Generate your first report from the Generate tab.
            </Typography>
          </Box>
        ) : (
          <TableContainer sx={{ overflowX: "auto" }}>
            <Table>
              <TableHead>
                <TableRow
                  sx={{
                    "& th": {
                      fontSize: 11,
                      fontWeight: 700,
                      letterSpacing: "0.06em",
                      textTransform: "uppercase",
                      color: "#94A3B8",
                      py: 1.5,
                      borderBottom: "1px solid rgba(148,163,184,0.15)",
                      background: "rgba(248,250,252,0.7)",
                    },
                  }}
                >
                  <TableCell>Report</TableCell>
                  <TableCell>Format</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Size</TableCell>
                  <TableCell>Generated</TableCell>
                  <TableCell>Expires</TableCell>
                  <TableCell />
                </TableRow>
              </TableHead>
              <TableBody>
                {reports.map((r) => {
                  const rowCfg = STATUS_ROW[r.status] || {
                    dot: "#CBD5E1",
                    bg: "transparent",
                    border: "transparent",
                  };
                  const fmtColor = FORMAT_COLOR[r.output_format] || "#64748B";
                  return (
                    <TableRow
                      key={r.id}
                      sx={{
                        "&:hover td": { background: "rgba(15,118,110,0.03)" },
                        "& td": {
                          py: 1.5,
                          borderBottom: "1px solid rgba(148,163,184,0.1)",
                        },
                      }}
                    >
                      {/* Report name + date range */}
                      <TableCell>
                        <Stack
                          direction="row"
                          alignItems="center"
                          spacing={1.25}
                        >
                          <Box
                            sx={{
                              width: 7,
                              height: 7,
                              borderRadius: "50%",
                              background: rowCfg.dot,
                              flexShrink: 0,
                            }}
                          />
                          <Box>
                            <Typography
                              sx={{
                                fontSize: 13,
                                fontWeight: 600,
                                color: "#1E293B",
                              }}
                            >
                              {REPORT_TYPES.find(
                                (t) => t.value === r.report_type,
                              )?.label || r.report_type}
                            </Typography>
                            <Typography
                              sx={{ fontSize: 11, color: "#94A3B8", mt: 0.2 }}
                            >
                              {r.parameters?.date_from} –{" "}
                              {r.parameters?.date_to}
                            </Typography>
                          </Box>
                        </Stack>
                      </TableCell>

                      {/* Format badge */}
                      <TableCell>
                        <Box
                          sx={{
                            display: "inline-flex",
                            alignItems: "center",
                            justifyContent: "center",
                            px: 1.25,
                            py: 0.4,
                            borderRadius: 1.5,
                            background: `${fmtColor}18`,
                            border: `1px solid ${fmtColor}35`,
                          }}
                        >
                          <Typography
                            sx={{
                              fontSize: 10.5,
                              fontWeight: 700,
                              color: fmtColor,
                              letterSpacing: "0.04em",
                            }}
                          >
                            {FORMAT_LABELS[r.output_format] || r.output_format}
                          </Typography>
                        </Box>
                      </TableCell>

                      {/* Status pill */}
                      <TableCell>
                        <Box
                          sx={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 0.75,
                            px: 1.25,
                            py: 0.4,
                            borderRadius: 999,
                            background: rowCfg.bg,
                            border: `1px solid ${rowCfg.border}`,
                          }}
                        >
                          <Box
                            sx={{
                              width: 5.5,
                              height: 5.5,
                              borderRadius: "50%",
                              background: rowCfg.dot,
                            }}
                          />
                          <Typography
                            sx={{
                              fontSize: 11,
                              fontWeight: 600,
                              color: rowCfg.dot,
                            }}
                          >
                            {STATUS_BADGE[r.status]?.label || r.status}
                          </Typography>
                        </Box>
                      </TableCell>

                      <TableCell>
                        <Typography sx={{ fontSize: 12, color: "#64748B" }}>
                          {fmtSize(r.file_size)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography sx={{ fontSize: 12, color: "#64748B" }}>
                          {fmtDate(r.created_at)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography sx={{ fontSize: 12, color: "#94A3B8" }}>
                          {fmtDate(r.expires_at)}
                        </Typography>
                      </TableCell>

                      {/* Actions */}
                      <TableCell align="right">
                        <Stack
                          direction="row"
                          spacing={0.5}
                          justifyContent="flex-end"
                        >
                          {r.status === "ready" && (
                            <Tooltip title="Download">
                              <IconButton
                                size="small"
                                onClick={() => handleDownload(r)}
                                sx={{
                                  color: "#10B981",
                                  border: "1px solid rgba(16,185,129,0.25)",
                                  borderRadius: 1.5,
                                  p: 0.6,
                                  "&:hover": {
                                    background: "rgba(16,185,129,0.08)",
                                  },
                                }}
                              >
                                <FileDownload sx={{ fontSize: 15 }} />
                              </IconButton>
                            </Tooltip>
                          )}
                          {r.status === "failed" && (
                            <Tooltip title="Retry">
                              <IconButton
                                size="small"
                                onClick={() => handleRetry(r)}
                                sx={{
                                  color: "#0EA5E9",
                                  border: "1px solid rgba(14,165,233,0.25)",
                                  borderRadius: 1.5,
                                  p: 0.6,
                                  "&:hover": {
                                    background: "rgba(14,165,233,0.08)",
                                  },
                                }}
                              >
                                <Refresh sx={{ fontSize: 15 }} />
                              </IconButton>
                            </Tooltip>
                          )}
                          <Tooltip title="Delete">
                            <IconButton
                              size="small"
                              onClick={() => setDeleteId(r.id)}
                              sx={{
                                color: "#EF4444",
                                border: "1px solid rgba(239,68,68,0.2)",
                                borderRadius: 1.5,
                                p: 0.6,
                                "&:hover": {
                                  background: "rgba(239,68,68,0.07)",
                                },
                              }}
                            >
                              <Delete sx={{ fontSize: 15 }} />
                            </IconButton>
                          </Tooltip>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {/* ── Pagination ── */}
        {total > PAGE_SIZE && (
          <Stack
            direction={{ xs: "column", sm: "row" }}
            justifyContent="flex-end"
            alignItems="center"
            spacing={1}
            sx={{ px: 3, py: 2, borderTop: "1px solid rgba(148,163,184,0.12)" }}
          >
            <Typography sx={{ fontSize: 12, color: "#94A3B8" }}>
              Page {page} of {Math.ceil(total / PAGE_SIZE)}
            </Typography>
            <Button
              size="small"
              disabled={page === 1}
              onClick={() => setPage((p) => p - 1)}
              sx={{
                borderRadius: 999,
                textTransform: "none",
                fontSize: 12,
                px: 1.5,
              }}
            >
              Prev
            </Button>
            <Button
              size="small"
              disabled={page >= Math.ceil(total / PAGE_SIZE)}
              onClick={() => setPage((p) => p + 1)}
              sx={{
                borderRadius: 999,
                textTransform: "none",
                fontSize: 12,
                px: 1.5,
              }}
            >
              Next
            </Button>
          </Stack>
        )}
      </Box>

      {/* ── Delete confirm ── */}
      <Dialog
        open={!!deleteId}
        onClose={() => setDeleteId(null)}
        maxWidth="xs"
        PaperProps={{
          sx: { borderRadius: 3, border: "1px solid rgba(239,68,68,0.2)" },
        }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <Box
              sx={{
                width: 32,
                height: 32,
                borderRadius: 2,
                background: "rgba(239,68,68,0.1)",
                display: "grid",
                placeItems: "center",
              }}
            >
              <Delete sx={{ fontSize: 16, color: "#EF4444" }} />
            </Box>
            <Typography sx={{ fontWeight: 700, fontSize: 15 }}>
              Delete report?
            </Typography>
          </Stack>
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: 13, color: "#64748B" }}>
            The file will be permanently deleted from storage. This cannot be
            undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button
            onClick={() => setDeleteId(null)}
            sx={{ borderRadius: 999, textTransform: "none", fontSize: 13 }}
          >
            Cancel
          </Button>
          <Button
            color="error"
            variant="contained"
            onClick={handleDelete}
            disabled={deleteLoading}
            sx={{
              borderRadius: 999,
              textTransform: "none",
              fontSize: 13,
              px: 2.5,
            }}
          >
            {deleteLoading ? (
              <CircularProgress size={14} color="inherit" />
            ) : (
              "Delete"
            )}
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
}

// ─── Schedules Tab ────────────────────────────────────────────
function SchedulesTab({ headers, activeOrg, onToast }) {
  const [schedules, setSchedules] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [recipientError, setRecipientError] = useState("");
  const [deviceOptions, setDeviceOptions] = useState([]);
  const [deviceLoading, setDeviceLoading] = useState(false);
  const [deviceLoadError, setDeviceLoadError] = useState("");
  const [auditFilters, setAuditFilters] = useState({ entityTypes: [], actions: [] });

  const [form, setForm] = useState({
    report_type: "alert_summary",
    frequency: "weekly",
    output_formats: ["csv"],
    recipients: "",
    date_window_days: 7,
    device_id: "",
    severity: "",
    entity_type: "",
    action_filter: "",
  });

  const typeMeta =
    REPORT_TYPES.find((t) => t.value === form.report_type) || REPORT_TYPES[0];

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_BASE}/reports/schedules`, { headers });
      setSchedules(res.data);
    } catch (err) {
      console.error("Load schedules failed", err);
      onToast?.(
        err?.response?.data?.error || "Failed to load report schedules.",
        "error",
      );
    } finally {
      setLoading(false);
    }
  }, [headers, onToast]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (!activeOrg?.id) return;

    const loadDevices = async () => {
      try {
        setDeviceLoading(true);
        setDeviceLoadError("");
        const res = await axios.get(`${API_BASE}/devices`, { headers });
        const rows = Array.isArray(res?.data) ? res.data : [];
        const normalized = rows
          .map((device) => ({
            id: String(device?.id || "").trim(),
            name: String(device?.name || "").trim(),
          }))
          .filter((device) => device.id);
        setDeviceOptions(normalized);
      } catch (err) {
        setDeviceLoadError(
          err?.response?.data?.error || "Unable to load devices",
        );
        onToast?.(
          err?.response?.data?.error ||
            "Unable to load devices for schedule filters.",
          "error",
        );
        setDeviceOptions([]);
      } finally {
        setDeviceLoading(false);
      }
    };

    loadDevices();
  }, [activeOrg?.id, headers, onToast]);

  useEffect(() => {
    if (!activeOrg?.id || form.report_type !== "audit") return;

    const loadAuditFilters = async () => {
      try {
        const res = await axios.get(`${API_BASE}/reports/audit/filters`, {
          headers,
          params: form.entity_type
            ? { entity_type: form.entity_type }
            : undefined,
        });

        const nextEntityTypes = res.data.entity_types || [];
        const nextActions = res.data.actions || [];

        setAuditFilters({
          entityTypes: nextEntityTypes,
          actions: nextActions,
        });

        if (
          form.action_filter &&
          !nextActions.includes(form.action_filter)
        ) {
          setForm((prev) => ({ ...prev, action_filter: "" }));
        }
      } catch (err) {
        console.error("Failed to load audit filters", err);
      }
    };

    loadAuditFilters();
  }, [activeOrg?.id, headers, form.report_type, form.entity_type]);

  useEffect(() => {
    if (
      form.report_type !== "audit" &&
      (form.entity_type || form.action_filter)
    ) {
      setForm((prev) => ({
        ...prev,
        entity_type: "",
        action_filter: "",
      }));
    }
  }, [form.report_type, form.entity_type, form.action_filter]);

  useEffect(() => {
    if (!typeMeta.hasSeverity && form.severity) {
      setForm((f) => ({ ...f, severity: "" }));
    }
  }, [typeMeta.hasSeverity, form.severity]);

  const handleToggle = async (schedule) => {
    try {
      await axios.patch(
        `${API_BASE}/reports/schedules/${schedule.id}`,
        { enabled: !schedule.enabled },
        { headers },
      );
      load();
      onToast?.(
        `Schedule ${schedule.enabled ? "disabled" : "enabled"} successfully.`,
        "success",
      );
    } catch (err) {
      console.error("Toggle failed", err);
      onToast?.(
        err?.response?.data?.error || "Failed to update schedule status.",
        "error",
      );
    }
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    try {
      await axios.delete(`${API_BASE}/reports/schedules/${deleteId}`, {
        headers,
      });
      setDeleteId(null);
      load();
      onToast?.("Schedule deleted successfully.", "success");
    } catch (err) {
      console.error("Delete schedule failed", err);
      onToast?.(
        err?.response?.data?.error || "Failed to delete schedule.",
        "error",
      );
    }
  };

  const handleCreate = async () => {
    if (!activeOrg?.id) {
      onToast?.("No active organization selected.", "error");
      return;
    }

    if (!REPORT_TYPES.some((t) => t.value === form.report_type)) {
      onToast?.("Please select a valid report type.", "error");
      return;
    }

    if (!FREQUENCIES.includes(form.frequency)) {
      onToast?.("Please select a valid schedule frequency.", "error");
      return;
    }

    const allowedScheduleFormats = OUTPUT_FORMATS.filter(
      (f) => f.value !== "json",
    ).map((f) => f.value);
    if (
      !Array.isArray(form.output_formats) ||
      form.output_formats.length === 0 ||
      form.output_formats.some((f) => !allowedScheduleFormats.includes(f))
    ) {
      onToast?.("Please select at least one valid output format.", "error");
      return;
    }

    if (![1, 7, 14, 30].includes(Number(form.date_window_days))) {
      onToast?.("Please select a valid data window.", "error");
      return;
    }

    if (typeMeta.hasDevice && form.device_id) {
      const isValidDevice = deviceOptions.some((d) => d.id === form.device_id);
      if (!isValidDevice) {
        onToast?.(
          "Selected device is invalid for the current organization.",
          "error",
        );
        return;
      }
    }

    if (form.severity && !typeMeta.hasSeverity) {
      onToast?.(
        "Severity filter is not supported for this report type.",
        "error",
      );
      return;
    }

    if (form.severity && !VALID_SEVERITIES.includes(form.severity)) {
      onToast?.("Please select a valid severity value.", "error");
      return;
    }

    if (typeMeta.hasAuditFilters) {
      if (
        form.entity_type &&
        auditFilters.entityTypes.length &&
        !auditFilters.entityTypes.includes(form.entity_type)
      ) {
        onToast?.("Please select a valid audit entity type.", "error");
        return;
      }

      if (
        form.action_filter &&
        auditFilters.actions.length &&
        !auditFilters.actions.includes(form.action_filter)
      ) {
        onToast?.("Please select a valid audit action.", "error");
        return;
      }
    }

    const recipientList = form.recipients
      .split(",")
      .map((e) => e.trim())
      .filter(Boolean);
    if (!recipientList.length) {
      setRecipientError("At least one recipient email is required.");
      onToast?.("Please provide at least one recipient email.", "error");
      return;
    }

    const invalidEmails = recipientList.filter(
      (email) => !EMAIL_REGEX.test(email),
    );
    if (invalidEmails.length) {
      setRecipientError(
        `Invalid recipient emails: ${invalidEmails.join(", ")}`,
      );
      onToast?.("One or more recipient emails are invalid.", "error");
      return;
    }

    setRecipientError("");

    const scheduleParameters = { date_window_days: form.date_window_days };
    if (typeMeta.hasDevice && form.device_id) {
      scheduleParameters.device_id = form.device_id;
    }
    if (typeMeta.hasSeverity && form.severity) {
      scheduleParameters.severity = form.severity;
    }
    if (typeMeta.hasAuditFilters) {
      if (form.entity_type) scheduleParameters.entity_type = form.entity_type;
      if (form.action_filter) scheduleParameters.action = form.action_filter;
    }

    setSaving(true);
    try {
      await axios.post(
        `${API_BASE}/reports/schedules/${activeOrg.id}`,
        {
          report_type: form.report_type,
          frequency: form.frequency,
          output_formats: form.output_formats,
          recipients: recipientList,
          parameters: scheduleParameters,
        },
        { headers },
      );
      setShowForm(false);
      setRecipientError("");
      setForm({
        report_type: "alert_summary",
        frequency: "weekly",
        output_formats: ["csv"],
        recipients: "",
        date_window_days: 7,
        device_id: "",
        severity: "",
        entity_type: "",
        action_filter: "",
      });
      load();
      onToast?.("Report schedule created successfully.", "success");
    } catch (err) {
      console.error("Create schedule failed", err);
      onToast?.(
        err?.response?.data?.error || "Failed to create report schedule.",
        "error",
      );
    } finally {
      setSaving(false);
    }
  };

  // ── CHANGED: toggle a format in/out of the schedule form array ──
  const toggleScheduleFormat = (fmt) => {
    setForm((f) => {
      const current = f.output_formats;
      const next = current.includes(fmt)
        ? current.length > 1
          ? current.filter((x) => x !== fmt)
          : current
        : [...current, fmt];
      return { ...f, output_formats: next };
    });
  };

  const FORMAT_META_S = {
    csv: {
      color: "#10B981",
      bg: "rgba(16,185,129,0.10)",
      border: "rgba(16,185,129,0.30)",
    },
    excel: {
      color: "#0EA5E9",
      bg: "rgba(14,165,233,0.10)",
      border: "rgba(14,165,233,0.30)",
    },
    pdf: {
      color: "#F59E0B",
      bg: "rgba(245,158,11,0.10)",
      border: "rgba(245,158,11,0.30)",
    },
  };

  const FREQ_COLOR = {
    daily: "#A78BFA",
    weekly: "#0EA5E9",
    monthly: "#10B981",
  };

  const SLabel = ({ children }) => (
    <Typography
      sx={{
        fontSize: 10,
        fontWeight: 700,
        letterSpacing: "0.1em",
        textTransform: "uppercase",
        color: "rgba(100,116,139,0.9)",
        mb: 0.6,
      }}
    >
      {children}
    </Typography>
  );

  return (
    <Paper
      elevation={0}
      sx={{
        borderRadius: 3,
        overflow: "hidden",
        border: "1px solid rgba(148,163,184,0.18)",
        boxShadow: "0 4px 24px rgba(15,23,42,0.07)",
      }}
    >
      {/* ── Dark header stripe ── */}
      <Box
        sx={{
          px: 3,
          py: 2.5,
          background:
            "linear-gradient(135deg, rgba(15,23,42,0.96), rgba(15,118,110,0.88) 60%, rgba(8,47,73,0.92))",
          position: "relative",
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            background:
              "linear-gradient(120deg, transparent 0%, rgba(255,255,255,0.06) 40%, transparent 65%)",
            pointerEvents: "none",
          }}
        />
        <Stack
          direction={{ xs: "column", sm: "row" }}
          justifyContent="space-between"
          alignItems={{ xs: "flex-start", sm: "center" }}
          spacing={2}
        >
          <Stack direction="row" alignItems="center" spacing={1.5}>
            <Box
              sx={{
                width: 36,
                height: 36,
                borderRadius: 2,
                display: "grid",
                placeItems: "center",
                background: "rgba(255,255,255,0.12)",
                border: "1px solid rgba(255,255,255,0.18)",
              }}
            >
              <Schedule sx={{ fontSize: 18, color: "#7DD3FC" }} />
            </Box>
            <Box>
              <Typography
                sx={{
                  color: "#F8FAFC",
                  fontWeight: 700,
                  fontSize: 15,
                  lineHeight: 1.2,
                }}
              >
                Report schedules
              </Typography>
              <Typography
                sx={{ color: "rgba(226,232,240,0.65)", fontSize: 12, mt: 0.2 }}
              >
                Reports are emailed to recipients automatically
              </Typography>
            </Box>
          </Stack>
          <Button
            startIcon={
              showForm ? (
                <Close sx={{ fontSize: 16 }} />
              ) : (
                <Add sx={{ fontSize: 16 }} />
              )
            }
            onClick={() =>
              setShowForm((v) => {
                if (v) setRecipientError("");
                return !v;
              })
            }
            sx={{
              px: 2,
              py: 0.9,
              borderRadius: 999,
              textTransform: "none",
              fontWeight: 700,
              fontSize: 13,
              color: "#082F49",
              background: "linear-gradient(135deg, #FDE68A, #F59E0B)",
              boxShadow: "0 8px 20px rgba(245,158,11,0.25)",
              "&:hover": {
                background: "linear-gradient(135deg, #FCD34D, #D97706)",
              },
            }}
          >
            {showForm ? "Cancel" : "New schedule"}
          </Button>
        </Stack>
      </Box>

      {/* ── New schedule form ── */}
      {showForm && (
        <Box
          sx={{
            px: 3,
            py: 3,
            borderBottom: "1px solid rgba(148,163,184,0.14)",
            background:
              "linear-gradient(135deg, rgba(15,118,110,0.03), rgba(8,47,73,0.02))",
          }}
        >
          <Stack direction="row" alignItems="center" spacing={1} mb={2.5}>
            <Box
              sx={{
                width: 4,
                height: 20,
                borderRadius: 1,
                background: "linear-gradient(180deg, #0F766E, #0EA5E9)",
              }}
            />
            <Typography
              sx={{ fontWeight: 700, fontSize: 13, color: "#1E293B" }}
            >
              New schedule
            </Typography>
          </Stack>

          <Box
            display="grid"
            gridTemplateColumns={{
              xs: "1fr",
              sm: "1fr 1fr",
              md: "1fr 1fr 1fr",
            }}
            gap={2}
            mb={2.5}
          >
            <Box>
              <SLabel>Report type</SLabel>
              <FormControl size="small" fullWidth>
                <Select
                  value={form.report_type}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, report_type: e.target.value }))
                  }
                  sx={{ borderRadius: 2 }}
                >
                  {REPORT_TYPES.map((t) => (
                    <MenuItem key={t.value} value={t.value}>
                      {t.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
            <Box>
              <SLabel>Frequency</SLabel>
              <FormControl size="small" fullWidth>
                <Select
                  value={form.frequency}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, frequency: e.target.value }))
                  }
                  sx={{ borderRadius: 2 }}
                >
                  {FREQUENCIES.map((f) => (
                    <MenuItem key={f} value={f}>
                      {f.charAt(0).toUpperCase() + f.slice(1)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
            <Box>
              <SLabel>Data window</SLabel>
              <FormControl size="small" fullWidth>
                <Select
                  value={form.date_window_days}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, date_window_days: e.target.value }))
                  }
                  sx={{ borderRadius: 2 }}
                >
                  <MenuItem value={1}>Last 1 day</MenuItem>
                  <MenuItem value={7}>Last 7 days</MenuItem>
                  <MenuItem value={14}>Last 14 days</MenuItem>
                  <MenuItem value={30}>Last 30 days</MenuItem>
                </Select>
              </FormControl>
            </Box>
            {typeMeta.hasDevice && (
              <Box>
                <SLabel>Device (optional)</SLabel>
                <TextField
                  select
                  size="small"
                  fullWidth
                  value={form.device_id}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, device_id: e.target.value }))
                  }
                  disabled={deviceLoading}
                  sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                >
                  <MenuItem value="">All devices</MenuItem>
                  {deviceOptions.map((device) => (
                    <MenuItem key={device.id} value={device.id}>
                      {device.name ? `${device.name} (${device.id})` : device.id}
                    </MenuItem>
                  ))}
                </TextField>
                <Typography
                  sx={{
                    mt: 0.75,
                    fontSize: 11.5,
                    color: deviceLoadError ? "#B91C1C" : "#64748B",
                  }}
                >
                  {deviceLoading
                    ? "Loading devices..."
                    : deviceLoadError
                      ? deviceLoadError
                      : `${deviceOptions.length} device${deviceOptions.length === 1 ? "" : "s"} available`}
                </Typography>
              </Box>
            )}
            {typeMeta.hasSeverity && (
              <Box>
                <SLabel>Severity (optional)</SLabel>
                <FormControl size="small" fullWidth>
                  <Select
                    value={form.severity}
                    onChange={(e) =>
                      setForm((f) => ({ ...f, severity: e.target.value }))
                    }
                    sx={{ borderRadius: 2 }}
                  >
                    <MenuItem value="">All severities</MenuItem>
                    {VALID_SEVERITIES.map((s) => (
                      <MenuItem key={s} value={s}>
                        {s.charAt(0).toUpperCase() + s.slice(1)}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
            )}
            {typeMeta.hasAuditFilters && (
              <Box>
                <SLabel>Entity type (optional)</SLabel>
                <FormControl size="small" fullWidth>
                  <Select
                    value={form.entity_type}
                    onChange={(e) =>
                      setForm((f) => ({
                        ...f,
                        entity_type: e.target.value,
                        action_filter: "",
                      }))
                    }
                    sx={{ borderRadius: 2 }}
                  >
                    <MenuItem value="">All entity types</MenuItem>
                    {auditFilters.entityTypes.map((entityType) => (
                      <MenuItem key={entityType} value={entityType}>
                        {entityType}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
            )}
            {typeMeta.hasAuditFilters && (
              <Box>
                <SLabel>Action (optional)</SLabel>
                <FormControl size="small" fullWidth>
                  <Select
                    value={form.action_filter}
                    onChange={(e) =>
                      setForm((f) => ({ ...f, action_filter: e.target.value }))
                    }
                    sx={{ borderRadius: 2 }}
                  >
                    <MenuItem value="">All actions</MenuItem>
                    {auditFilters.actions.map((auditAction) => (
                      <MenuItem key={auditAction} value={auditAction}>
                        {auditAction}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
            )}
          </Box>

          <Box mb={2.5}>
            <SLabel>
              Output formats
              {/* badge showing selection count */}
              <Box
                component="span"
                sx={{
                  ml: 1,
                  px: 0.9,
                  py: 0.15,
                  borderRadius: 999,
                  background: "rgba(15,118,110,0.12)",
                  color: "#0F766E",
                  fontSize: 9,
                  fontWeight: 800,
                  letterSpacing: "0.04em",
                  verticalAlign: "middle",
                }}
              >
                {form.output_formats.length} selected
              </Box>
            </SLabel>
            <Stack direction="row" spacing={1}>
              {OUTPUT_FORMATS.filter((f) => f.value !== "json").map((f) => {
                // ── CHANGED: active = array includes this value ──
                const active = form.output_formats.includes(f.value);
                const meta = FORMAT_META_S[f.value];

                return (
                  <Box
                    key={f.value}
                    onClick={() => toggleScheduleFormat(f.value)}
                    sx={{
                      px: 1.75,
                      py: 0.8,
                      borderRadius: 2,
                      cursor: "pointer",
                      border: active
                        ? `1.5px solid ${meta.border}`
                        : "1.5px solid rgba(148,163,184,0.22)",
                      background: active ? meta.bg : "rgba(248,250,252,0.6)",
                      display: "flex",
                      alignItems: "center",
                      gap: 0.75,
                      transition: "all 0.15s ease",
                      "&:hover": {
                        borderColor: meta.border,
                        background: meta.bg,
                      },
                    }}
                  >
                    {/* ── CHANGED: checkbox indicator ── */}
                    <Box
                      sx={{
                        width: 13,
                        height: 13,
                        borderRadius: 0.5,
                        flexShrink: 0,
                        display: "grid",
                        placeItems: "center",
                        border: active
                          ? `2px solid ${meta.color}`
                          : "2px solid #CBD5E1",
                        background: active ? meta.color : "transparent",
                        transition: "all 0.15s ease",
                      }}
                    >
                      {active && (
                        <Box
                          component="svg"
                          viewBox="0 0 10 8"
                          sx={{ width: 7, height: 7 }}
                        >
                          <path
                            d="M1 4l2.5 2.5L9 1"
                            stroke="white"
                            strokeWidth="1.8"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            fill="none"
                          />
                        </Box>
                      )}
                    </Box>
                    <Typography
                      sx={{
                        fontSize: 12,
                        fontWeight: active ? 700 : 500,
                        color: active ? meta.color : "#334155",
                      }}
                    >
                      {f.label}
                    </Typography>
                  </Box>
                );
              })}
            </Stack>
          </Box>

          <Box mb={2.5}>
            <SLabel>Recipients</SLabel>
            <TextField
              size="small"
              fullWidth
              placeholder="ops@company.com, admin@company.com"
              value={form.recipients}
              onChange={(e) => {
                setForm((f) => ({ ...f, recipients: e.target.value }));
                if (recipientError) setRecipientError("");
              }}
              error={Boolean(recipientError)}
              helperText={
                recipientError || "Separate multiple emails with commas."
              }
              sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
            />
          </Box>

          <Stack direction="row" spacing={1}>
            <Button
              onClick={() => {
                setShowForm(false);
                setRecipientError("");
              }}
              sx={{
                borderRadius: 999,
                textTransform: "none",
                fontSize: 13,
                px: 2,
              }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleCreate}
              disabled={
                saving ||
                !form.recipients.trim() ||
                form.output_formats.length === 0
              }
              sx={{
                borderRadius: 999,
                textTransform: "none",
                fontWeight: 700,
                fontSize: 13,
                px: 2.5,
                background: "linear-gradient(135deg, #0F766E, #0EA5E9)",
                boxShadow: "0 6px 18px rgba(15,118,110,0.25)",
                "&:hover": {
                  background: "linear-gradient(135deg, #0D6660, #0C96D6)",
                },
                "&.Mui-disabled": {
                  color: "#fff !important",
                  WebkitTextFillColor: "#fff", // fixes Chrome
                  opacity: 0.6,
                },
              }}
            >
              {saving ? (
                <CircularProgress size={14} color="inherit" />
              ) : (
                "Create schedule"
              )}
            </Button>
          </Stack>
        </Box>
      )}

      {/* ── Body ── */}
      {loading ? (
        <Box sx={{ background: "#fff" }}>
          <ReportsTableSkeleton columns={7} />
        </Box>
      ) : schedules.length === 0 ? (
        <Box
          sx={{
            py: 7,
            textAlign: "center",
            background:
              "radial-gradient(ellipse at center, rgba(15,118,110,0.04) 0%, transparent 70%)",
          }}
        >
          <Box
            sx={{
              width: 48,
              height: 48,
              borderRadius: 3,
              background: "rgba(15,118,110,0.08)",
              border: "1px solid rgba(15,118,110,0.15)",
              display: "grid",
              placeItems: "center",
              mx: "auto",
              mb: 2,
            }}
          >
            <Schedule sx={{ fontSize: 22, color: "#0F766E" }} />
          </Box>
          <Typography
            sx={{ fontWeight: 600, fontSize: 14, color: "#334155", mb: 0.5 }}
          >
            No schedules yet
          </Typography>
          <Typography sx={{ fontSize: 13, color: "#94A3B8" }}>
            Create one to automatically deliver reports by email.
          </Typography>
        </Box>
      ) : (
        <TableContainer sx={{ overflowX: "auto" }}>
          <Table>
            <TableHead>
              <TableRow
                sx={{
                  "& th": {
                    fontSize: 11,
                    fontWeight: 700,
                    letterSpacing: "0.06em",
                    textTransform: "uppercase",
                    color: "#94A3B8",
                    py: 1.5,
                    borderBottom: "1px solid rgba(148,163,184,0.15)",
                    background: "rgba(248,250,252,0.7)",
                  },
                }}
              >
                <TableCell>Report</TableCell>
                <TableCell>Frequency</TableCell>
                <TableCell>Formats</TableCell>
                <TableCell>Next run</TableCell>
                <TableCell>Recipients</TableCell>
                <TableCell>Active</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {schedules.map((s) => {
                const freqColor = FREQ_COLOR[s.frequency] || "#64748B";
                const scheduleDeviceId = s.parameters?.device_id;
                const scheduleSeverity = s.parameters?.severity;
                return (
                  <TableRow
                    key={s.id}
                    sx={{
                      "&:hover td": { background: "rgba(15,118,110,0.03)" },
                      "& td": {
                        py: 1.5,
                        borderBottom: "1px solid rgba(148,163,184,0.1)",
                      },
                    }}
                  >
                    {/* Report name */}
                    <TableCell>
                      <Stack direction="row" alignItems="center" spacing={1.25}>
                        <Box
                          sx={{
                            width: 7,
                            height: 7,
                            borderRadius: "50%",
                            background: s.enabled ? "#10B981" : "#CBD5E1",
                            flexShrink: 0,
                          }}
                        />
                        <Box>
                          <Typography
                            sx={{
                              fontSize: 13,
                              fontWeight: 600,
                              color: "#1E293B",
                            }}
                          >
                            {REPORT_TYPES.find((t) => t.value === s.report_type)
                              ?.label || s.report_type}
                          </Typography>
                          {(scheduleDeviceId || scheduleSeverity) && (
                            <Typography
                              sx={{
                                fontSize: 11,
                                color: "#64748B",
                                mt: 0.25,
                              }}
                            >
                              {scheduleDeviceId
                                ? `Device: ${scheduleDeviceId}`
                                : "All devices"}
                              {scheduleSeverity
                                ? ` • Severity: ${String(scheduleSeverity)
                                    .charAt(0)
                                    .toUpperCase()}${String(scheduleSeverity).slice(1)}`
                                : ""}
                            </Typography>
                          )}
                        </Box>
                      </Stack>
                    </TableCell>

                    {/* Frequency badge */}
                    <TableCell>
                      <Box
                        sx={{
                          display: "inline-flex",
                          alignItems: "center",
                          gap: 0.6,
                          px: 1.25,
                          py: 0.4,
                          borderRadius: 999,
                          background: `${freqColor}15`,
                          border: `1px solid ${freqColor}35`,
                        }}
                      >
                        <Schedule sx={{ fontSize: 11, color: freqColor }} />
                        <Typography
                          sx={{
                            fontSize: 11,
                            fontWeight: 600,
                            color: freqColor,
                          }}
                        >
                          {s.frequency?.charAt(0).toUpperCase() +
                            s.frequency?.slice(1)}
                        </Typography>
                      </Box>
                    </TableCell>

                    {/* Formats */}
                    <TableCell>
                      <Stack direction="row" spacing={0.5}>
                        {(s.output_formats || []).map((f) => {
                          const m = FORMAT_META_S[f] || {
                            color: "#64748B",
                            bg: "rgba(100,116,139,0.1)",
                            border: "rgba(100,116,139,0.3)",
                          };
                          return (
                            <Box
                              key={f}
                              sx={{
                                px: 1,
                                py: 0.3,
                                borderRadius: 1.5,
                                background: m.bg,
                                border: `1px solid ${m.border}`,
                              }}
                            >
                              <Typography
                                sx={{
                                  fontSize: 10,
                                  fontWeight: 700,
                                  color: m.color,
                                }}
                              >
                                {FORMAT_LABELS[f] || f}
                              </Typography>
                            </Box>
                          );
                        })}
                      </Stack>
                    </TableCell>

                    {/* Next run */}
                    <TableCell>
                      <Typography sx={{ fontSize: 12, color: "#64748B" }}>
                        {fmtDate(s.next_run_at)}
                      </Typography>
                    </TableCell>

                    {/* Recipients */}
                    <TableCell>
                      <Tooltip title={(s.recipients || []).join(", ")}>
                        <Box
                          sx={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 0.5,
                            px: 1.25,
                            py: 0.4,
                            borderRadius: 999,
                            background: "rgba(100,116,139,0.07)",
                            border: "1px solid rgba(148,163,184,0.2)",
                            cursor: "default",
                          }}
                        >
                          <Typography sx={{ fontSize: 11, color: "#475569" }}>
                            {(s.recipients || []).length === 1
                              ? s.recipients[0]
                              : `${s.recipients?.[0]} +${(s.recipients?.length || 1) - 1} more`}
                          </Typography>
                        </Box>
                      </Tooltip>
                    </TableCell>

                    {/* Toggle */}
                    <TableCell>
                      <Box
                        onClick={() => handleToggle(s)}
                        sx={{
                          display: "inline-flex",
                          alignItems: "center",
                          gap: 0.75,
                          px: 1.25,
                          py: 0.4,
                          borderRadius: 999,
                          cursor: "pointer",
                          background: s.enabled
                            ? "rgba(16,185,129,0.1)"
                            : "rgba(148,163,184,0.1)",
                          border: `1px solid ${s.enabled ? "rgba(16,185,129,0.35)" : "rgba(148,163,184,0.3)"}`,
                          transition: "all 0.15s ease",
                          "&:hover": { filter: "brightness(0.94)" },
                        }}
                      >
                        <Box
                          sx={{
                            width: 5.5,
                            height: 5.5,
                            borderRadius: "50%",
                            background: s.enabled ? "#10B981" : "#94A3B8",
                          }}
                        />
                        <Typography
                          sx={{
                            fontSize: 11,
                            fontWeight: 600,
                            color: s.enabled ? "#10B981" : "#64748B",
                          }}
                        >
                          {s.enabled ? "On" : "Off"}
                        </Typography>
                      </Box>
                    </TableCell>

                    {/* Delete */}
                    <TableCell align="right">
                      <Tooltip title="Delete schedule">
                        <IconButton
                          size="small"
                          onClick={() => setDeleteId(s.id)}
                          sx={{
                            color: "#EF4444",
                            border: "1px solid rgba(239,68,68,0.2)",
                            borderRadius: 1.5,
                            p: 0.6,
                            "&:hover": { background: "rgba(239,68,68,0.07)" },
                          }}
                        >
                          <Delete sx={{ fontSize: 15 }} />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* ── Delete confirm ── */}
      <Dialog
        open={!!deleteId}
        onClose={() => setDeleteId(null)}
        maxWidth="xs"
        PaperProps={{
          sx: { borderRadius: 3, border: "1px solid rgba(239,68,68,0.2)" },
        }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <Box
              sx={{
                width: 32,
                height: 32,
                borderRadius: 2,
                background: "rgba(239,68,68,0.1)",
                display: "grid",
                placeItems: "center",
              }}
            >
              <Delete sx={{ fontSize: 16, color: "#EF4444" }} />
            </Box>
            <Typography sx={{ fontWeight: 700, fontSize: 15 }}>
              Delete schedule?
            </Typography>
          </Stack>
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: 13, color: "#64748B" }}>
            This schedule will be permanently removed and no further reports
            will be sent.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button
            onClick={() => setDeleteId(null)}
            sx={{ borderRadius: 999, textTransform: "none", fontSize: 13 }}
          >
            Cancel
          </Button>
          <Button
            color="error"
            variant="contained"
            onClick={handleDelete}
            sx={{
              borderRadius: 999,
              textTransform: "none",
              fontSize: 13,
              px: 2.5,
            }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
}

// ─── Main Page ────────────────────────────────────────────────
export default function ReportsPage() {
  const { activeOrg } = useAuth();
  const token = localStorage.getItem("authToken");
  const [tab, setTab] = useState(0);
  const [historyRefresh, setHistoryRefresh] = useState(0);
  const [toast, setToast] = useState({
    open: false,
    message: "",
    severity: "info",
  });

  const [stats, setStats] = useState({
    total: null,
    ready: null,
    failed: null,
  });

  const headers = {
    Authorization: `Bearer ${token}`,
    ...(activeOrg?.id && { "X-Org-ID": activeOrg.id }),
  };

  const fetchStats = useCallback(async () => {
    try {
      const res = await axios.get(`${API_BASE}/reports?page=1&page_size=200`, {
        headers,
      });
      const reports = res.data.data ?? [];
      const total = res.data.pagination?.total ?? reports.length;
      const ready = reports.filter((r) => r.status === "ready").length;
      const failed = reports.filter((r) => r.status === "failed").length;
      setStats({ total, ready, failed });
    } catch (err) {
      console.error("Stats fetch failed", err);
    }
  }, [activeOrg]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (activeOrg) fetchStats();
  }, [fetchStats, historyRefresh]);

  const fmt = (n) => (n === null ? "—" : String(n));

  const heroMetrics = [
    {
      label: "Total reports",
      value: fmt(stats.total),
      icon: <Assessment fontSize="small" />,
      tone: "#0EA5E9",
      bg: "rgba(14,165,233,0.18)",
    },
    {
      label: "Ready",
      value: fmt(stats.ready),
      icon: <CheckCircleOutline fontSize="small" />,
      tone: "#10B981",
      bg: "rgba(16,185,129,0.18)",
    },
    {
      label: "Failed",
      value: fmt(stats.failed),
      icon: <ErrorOutline fontSize="small" />,
      tone: "#EF4444",
      bg: "rgba(239,68,68,0.18)",
    },
  ];

  const refreshHistory = useCallback(() => {
    setHistoryRefresh((n) => n + 1);
  }, []);

  const showToast = useCallback((message, severity = "info") => {
    setToast({
      open: true,
      message,
      severity,
    });
  }, []);

  if (!activeOrg) {
    return (
      <Box p={4}>
        <Typography color="text.secondary">
          No active organization selected.
        </Typography>
      </Box>
    );
  }

  return (
    <>
      <Box
        sx={{
          minHeight: "100%",
          pt: { xs: 5, sm: 5, md: 1 },
          pb: 4,
          px: { xs: 1, sm: 2, md: 3 },
          background:
            "radial-gradient(circle at top left, rgba(14, 165, 233, 0.14), transparent 30%), radial-gradient(circle at top right, rgba(16, 185, 129, 0.12), transparent 28%), linear-gradient(180deg, #f8fbff 0%, #eef4f8 100%)",
        }}
      >
        <ReportsHeroBanner
          tab={tab}
          onTabChange={setTab}
          heroMetrics={heroMetrics}
        />

        <Box sx={{ width: "100%" }}>
          {tab === 0 && (
            <GenerateTab
              headers={headers}
              onGenerated={refreshHistory}
              activeOrg={activeOrg}
              onToast={showToast}
            />
          )}
          {tab === 1 && (
            <HistoryTab
              headers={headers}
              refresh={historyRefresh}
              onRefresh={refreshHistory}
              onToast={showToast}
              activeOrg={activeOrg}
            />
          )}

          {tab === 2 && (
            <SchedulesTab
              headers={headers}
              activeOrg={activeOrg}
              onToast={showToast}
            />
          )}
        </Box>
      </Box>

      <Snackbar
        open={toast.open}
        autoHideDuration={4000}
        onClose={() => setToast((t) => ({ ...t, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert severity={toast.severity} variant="filled">
          {toast.message}
        </Alert>
      </Snackbar>

      <ReportAgentChat />
    </>
  );
}
